/**
 * Event integration layer for frontend.
 *
 * Wraps the @smartwiz/debug-events package for frontend use.
 * Provides singleton instances of SessionManager and DebugEventBus.
 */

import {
  SessionManager,
  DebugEventBus,
  type DebugEventType,
  type DebugEventAction,
  type EventSurface,
  type EventContext,
  type DebugEvent,
  type DebugTimelineMarker,
} from "@smartwiz/debug-events";

// Singleton instances
let sessionManagerInstance: SessionManager | null = null;
let eventBusInstance: DebugEventBus | null = null;

/**
 * Get or create the session manager singleton
 */
export function getSessionManager(): SessionManager {
  if (!sessionManagerInstance) {
    sessionManagerInstance = new SessionManager();
  }
  return sessionManagerInstance;
}

/**
 * Get or create the event bus singleton
 */
export function getEventBus(): DebugEventBus {
  if (!eventBusInstance) {
    eventBusInstance = new DebugEventBus(getSessionManager());
  }
  return eventBusInstance;
}

/**
 * Reset both instances (useful for testing or cleanup)
 */
export function resetDebugSystem(): void {
  sessionManagerInstance = null;
  eventBusInstance = null;
}

const TIMELINE_ACTION_MARKERS = new Set<DebugEvent["action"]>([
  "ONECLICK_STARTED",
  "ONECLICK_FORM_FILL_STARTED",
  "ONECLICK_FORM_FILL_COMPLETED",
  "ONECLICK_COMPLETED",
  "ONECLICK_FAILED",
  "INTEGRATION_CONNECTION_STARTED",
  "INTEGRATION_CONNECTION_COMPLETED",
  "INTEGRATION_CONNECTION_FAILED",
  "INTEGRATION_IMPORT_STARTED",
  "INTEGRATION_IMPORT_COMPLETED",
  "INTEGRATION_IMPORT_FAILED",
  "FORM_NAVIGATION_STEP",
  "FORM_VALIDATION_ERROR",
  "CLIENT_DOCUMENT_PROCESSING_STARTED",
  "CLIENT_DOCUMENT_PROCESSING_COMPLETED",
  "CLIENT_DOCUMENT_PROCESSING_FAILED",
  "DOCUMENT_UPLOADED",
  "DOCUMENT_PROCESSING_STARTED",
  "DOCUMENT_PROCESSING_COMPLETED",
  "DOCUMENT_PROCESSING_FAILED",
]);

function shouldMarkTimeline(event: DebugEvent): boolean {
  if (event.type === "page_view" || event.type === "error") return true;
  if (event.type === "action" && TIMELINE_ACTION_MARKERS.has(event.action)) {
    return true;
  }
  return false;
}

function mapMarkerKind(event: DebugEvent): DebugTimelineMarker["kind"] {
  if (event.type === "page_view") return "nav";
  if (event.type === "error") return "error";
  if (event.action.includes("FAILED") || event.action.includes("ERROR")) {
    return "error";
  }
  return "step";
}

function buildMarkerMeta(
  event: DebugEvent
): Record<string, string | number | boolean> | undefined {
  const safeMeta: Record<string, string | number | boolean> = {
    action: event.action,
    surface: event.surface,
  };

  const context = event.context;
  if (context) {
    const allowedKeys: (keyof typeof context)[] = [
      "route",
      "path",
      "pageUrl",
      "form",
      "formName",
      "errorCode",
      "step",
      "stage",
    ];
    for (const key of allowedKeys) {
      const value = context[key];
      if (value !== undefined && value !== null) {
        safeMeta[key as string] = value as string | number | boolean;
      }
    }
  }

  return Object.keys(safeMeta).length ? safeMeta : undefined;
}

function postTimelineMark(
  kind: DebugTimelineMarker["kind"],
  eventId: string,
  meta?: Record<string, string | number | boolean>
): void {
  if (typeof window === "undefined") return;
  window.postMessage(
    {
      type: "DEBUG_TIMELINE_MARK",
      kind,
      eventId,
      meta,
    },
    window.location.origin
  );
}

/**
 * Emit a debug event (convenience function)
 */
export function emitDebugEvent(
  type: DebugEventType,
  action: DebugEventAction,
  surface: EventSurface = "web",
  context?: EventContext
): void {
  const eventBus = getEventBus();
  const event = eventBus.emit({
    type,
    action,
    surface,
    context,
  });

  if (event && shouldMarkTimeline(event)) {
    postTimelineMark(mapMarkerKind(event), event.id, buildMarkerMeta(event));
  }
}
